import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, Globe, RefreshCw, Lightbulb, Mail, ChevronDown, ArrowUp, CheckCircle2, Clock, Users, Send } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import contactFormBg from "@/assets/contact-form-bg.jpg";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getAuditT } from "@/i18n/requestAudit";

const HUBSPOT_PORTAL_ID = "7030766";
const HUBSPOT_FORM_ID = "030d857d-cb5b-474b-8eea-6bd8919bdbd9";

const FEATURE_ICONS = [ClipboardCheck, Globe, RefreshCw, Lightbulb];
const STEP_ICONS = [Send, Clock, Users, CheckCircle2];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.1, ease: "easeOut" as const },
  }),
};

const RequestAudit = () => {
  const hubspotContainerRef = useRef<HTMLDivElement>(null);
  const formSectionRef = useRef<HTMLElement>(null);
  const [formLoaded, setFormLoaded] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const t = getAuditT(lang);

  const canonicalPath = lang === "es" ? "/es/solicita-tu-auditoria" : lang === "zh-cn" ? "/zh-cn/request-your-audit" : "/en/request-your-audit";

  // Lazy-load HubSpot form when section is in view
  useEffect(() => {
    const section = formSectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !formLoaded) {
          setFormLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [formLoaded]);

  useEffect(() => {
    if (!formLoaded) return;
    const container = hubspotContainerRef.current;
    if (!container) return;

    const loadForm = () => {
      if ((window as any).hbspt) {
        container.innerHTML = "";
        (window as any).hbspt.forms.create({
          portalId: HUBSPOT_PORTAL_ID,
          formId: HUBSPOT_FORM_ID,
          target: "#hubspot-audit-form",
        });
      }
    };

    if ((window as any).hbspt) {
      loadForm();
      return;
    }

    const script = document.createElement("script");
    script.src = "//js.hsforms.net/forms/embed/v2.js";
    script.async = true;
    script.onload = loadForm;
    document.body.appendChild(script);

    return () => {
      try { document.body.removeChild(script); } catch {}
    };
  }, [formLoaded]);

  // Sticky mobile CTA visibility
  useEffect(() => {
    const onScroll = () => {
      setShowMobileCta(window.scrollY > 500);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const SERVICE_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "GxP Audit Services",
    description: t.seo.description,
    provider: { "@type": "Organization", name: "QbD Group", url: "https://qbdgroup.com" },
    serviceType: "Audit & Compliance",
    areaServed: "Worldwide",
    url: `https://qbdgroup.com${canonicalPath}`,
  };

  const FAQ_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const TRUST_ICONS = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  ];

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        canonical={`https://qbdgroup.com${canonicalPath}`}
      >
        <script type="application/ld+json">{JSON.stringify(SERVICE_JSON_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSON_LD)}</script>
      </SEOHead>
      <BreadcrumbJsonLd items={[{ name: t.breadcrumb }]} />
      <Navbar />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 min-h-[420px] sm:min-h-[480px] flex items-end overflow-hidden">
          <div className="absolute inset-0 bg-primary" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/90 to-primary" />
          <div className="container mx-auto px-6 lg:px-10 relative z-10">
            <nav className="text-sm text-primary-foreground/50 mb-8">
              <a href={prefix} className="hover:text-primary-foreground/80 transition-colors">{lang === "es" ? "Inicio" : "Home"}</a>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-foreground/80 font-medium">{t.breadcrumb}</span>
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground tracking-tight leading-tight mb-6">
                {t.heroTitle}
              </h1>
              <p className="text-lg sm:text-xl font-semibold text-primary-foreground/90 mb-4">
                {t.heroTagline}
              </p>
              <p className="text-primary-foreground/60 leading-relaxed mb-3">
                {t.heroDesc}
              </p>
              <p className="text-primary-foreground/60 leading-relaxed mb-8">
                {t.heroTrust}
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-3 rounded-full transition-colors"
              >
                {t.heroCta}
              </a>
            </motion.div>
          </div>
        </section>

        {/* Why QbD Group */}
        <section className="py-16 sm:py-24 bg-secondary/40">
          <div className="container mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-sm font-semibold text-accent tracking-widest uppercase">{t.whyKicker}</span>
                <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-6">
                  {t.whyTitle}
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">{t.whyIntro}</strong></p>
                  {t.whyBody.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 mt-8 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-3 rounded-full transition-colors"
                >
                  {t.whyCta}
                </a>
              </motion.div>

              {/* Feature cards */}
              <div className="grid grid-cols-2 gap-4">
                {t.features.map((feat, i) => {
                  const Icon = FEATURE_ICONS[i];
                  return (
                    <motion.div
                      key={i}
                      custom={i}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={cardVariants}
                      className="bg-accent/10 rounded-2xl p-6 text-center flex flex-col items-center gap-3 hover:bg-accent/15 hover:shadow-md transition-all duration-300"
                    >
                      <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center">
                        <Icon size={24} strokeWidth={1.5} className="text-accent" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground leading-snug">{feat.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{feat.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* What to Expect Timeline */}
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-accent tracking-widest uppercase">{t.timelineKicker}</span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">{t.timelineTitle}</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">{t.timelineSubtitle}</p>
            </motion.div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {t.steps.map((step, i) => {
                const Icon = STEP_ICONS[i];
                return (
                  <motion.div
                    key={i}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={cardVariants}
                    className="relative text-center"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon size={24} strokeWidth={1.5} className="text-primary" />
                    </div>
                    <div className="absolute top-7 left-[calc(50%+2rem)] right-0 h-px bg-border hidden lg:block" style={{ display: i === t.steps.length - 1 ? "none" : undefined }} />
                    <p className="text-sm font-semibold text-foreground mb-1">{step.label}</p>
                    <p className="text-xs text-muted-foreground">{step.desc}</p>
                    <span className="inline-block mt-3 text-[10px] font-bold text-primary/50 uppercase tracking-widest">{t.stepPrefix} {i + 1}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA after timeline */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-center mt-12"
            >
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-3 rounded-full transition-colors text-sm"
              >
                <Send size={16} />
                {t.heroCta}
              </a>
            </motion.div>
          </div>
        </section>

        {/* Contact / HubSpot Form — lazy loaded */}
        <section id="contact" ref={formSectionRef} className="relative py-16 sm:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img src={contactFormBg} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-primary/85" />
          </div>
          <div className="container mx-auto px-6 lg:px-10 relative z-10">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Mail size={36} strokeWidth={1.5} className="text-accent mb-5" />
                <span className="text-sm font-semibold text-accent tracking-widest uppercase">{t.formKicker}</span>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight leading-tight mb-5">
                  {t.formTitle}
                </h2>
                <p className="text-primary-foreground/60 leading-relaxed mb-4">
                  {t.formDesc}
                </p>
                <p className="text-primary-foreground/60 leading-relaxed mb-8">
                  {t.formNoObligation}
                </p>

                {/* Trust indicators */}
                <div className="border-t border-primary-foreground/10 pt-8 space-y-5">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/40 mb-4">{t.trustTitle}</h3>
                  {t.trustItems.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center shrink-0 mt-0.5">
                        {TRUST_ICONS[i]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-primary-foreground/90">{item.title}</p>
                        <p className="text-xs text-primary-foreground/50">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-background rounded-2xl border border-border p-8 sm:p-10 shadow-sm"
              >
                <div
                  id="hubspot-audit-form"
                  ref={hubspotContainerRef}
                  className="min-h-[500px] [&_label]:text-sm [&_label]:font-medium [&_label]:text-foreground/80 [&_input]:rounded-full [&_input]:border-border [&_select]:rounded-full [&_select]:border-border [&_textarea]:rounded-xl [&_textarea]:border-border [&_.hs-submit]:mt-4 [&_.hs-button]:!bg-accent [&_.hs-button]:!border-accent [&_.hs-button]:rounded-full [&_.hs-button]:font-semibold [&_.hs-button]:px-8 [&_.hs-button]:py-3 [&_.hs-error-msgs]:text-xs [&_.hs-error-msgs]:text-destructive"
                >
                  {!formLoaded && (
                    <div className="flex items-center justify-center h-[500px]">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">{t.loading}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-center mb-12"
            >
              <span className="text-sm font-semibold text-accent tracking-widest uppercase">{t.faqKicker}</span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">{t.faqTitle}</h2>
              <p className="text-muted-foreground">{t.faqSubtitle}</p>
            </motion.div>
            <div className="space-y-3">
              {t.faqs.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="border border-border rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-secondary/30 transition-colors"
                  >
                    <span className="text-sm font-semibold text-foreground">{item.q}</span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-muted-foreground transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: openFaq === i ? "300px" : "0px" }}
                  >
                    <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA after FAQ */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-center mt-12 pt-8 border-t border-border"
            >
              <p className="text-muted-foreground mb-4 text-sm">{t.readyCta}</p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-3.5 rounded-full transition-colors shadow-lg shadow-accent/15"
              >
                <ClipboardCheck size={18} />
                {t.heroCta}
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Sticky mobile CTA */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden transition-all duration-300 ${showMobileCta ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}
      >
        <div className="bg-background/95 backdrop-blur-md border-t border-border px-4 py-3 flex items-center gap-3">
          <a
            href="#contact"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 py-3 rounded-full transition-colors text-sm"
          >
            {t.mobileCta}
          </a>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="w-11 h-11 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label="Back to top"
          >
            <ArrowUp size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default RequestAudit;
